import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getLoyaltyQuote,
  getLoyaltySettings,
} from "../api/loyaltyApi";
import { buildLoyaltyCartItems, toNonNegativeInteger } from "../utils/loyalty";
import { useLoyaltySettings } from "../context/LoyaltySettingsContext";

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Не вдалося оновити бонусний розрахунок. Спробуйте ще раз.";

const useCheckoutLoyalty = ({ cartItems = [], customer = null } = {}) => {
  const { settings: sharedSettings } = useLoyaltySettings();
  const [settings, setSettings] = useState(null);
  const [quote, setQuote] = useState(null);
  const [requestedBonusKopecks, setRequestedBonusKopecksState] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [conditionsNotice, setConditionsNotice] = useState("");
  const requestSequenceRef = useRef(0);

  const quoteCartItems = useMemo(
    () => buildLoyaltyCartItems(cartItems),
    [cartItems],
  );
  const cartSignature = useMemo(
    () =>
      quoteCartItems
        .map((item) => `${item.productId}:${item.quantity}`)
        .sort()
        .join("|"),
    [quoteCartItems],
  );

  const setRequestedBonusKopecks = useCallback((value) => {
    setRequestedBonusKopecksState(toNonNegativeInteger(value));
  }, []);

  useEffect(() => {
    if (customer) return;
    setRequestedBonusKopecksState(0);
  }, [customer]);

  useEffect(() => {
    const controller = new AbortController();

    getLoyaltySettings({ signal: controller.signal })
      .then(setSettings)
      .catch((settingsError) => {
        if (settingsError?.code !== "ERR_CANCELED") {
          console.error("Не вдалося завантажити правила програми лояльності:", settingsError);
        }
      });

    return () => controller.abort();
  }, []);

  const refreshQuote = useCallback(
    async ({ bonusKopecks = requestedBonusKopecks, signal } = {}) => {
      const requestId = ++requestSequenceRef.current;
      const normalizedBonusKopecks = customer ? toNonNegativeInteger(bonusKopecks) : 0;
      setLoading(true);
      setError("");

      try {
        const nextQuote = await getLoyaltyQuote(
          { cartItems, requestedBonusKopecks: normalizedBonusKopecks },
          { signal },
        );
        if (requestId !== requestSequenceRef.current) return nextQuote;

        setQuote(nextQuote);
        if (nextQuote.settings) setSettings(nextQuote.settings);

        const appliedBonusKopecks = customer ? nextQuote.appliedBonusKopecks : 0;
        if (appliedBonusKopecks !== normalizedBonusKopecks) {
          setConditionsNotice("Умови замовлення змінилися. Суму перераховано. Перевірте її перед підтвердженням.");
          setRequestedBonusKopecksState(appliedBonusKopecks);
        }

        return nextQuote;
      } catch (quoteError) {
        if (quoteError?.code === "ERR_CANCELED") throw quoteError;
        if (requestId === requestSequenceRef.current) {
          setError(getErrorMessage(quoteError));
        }
        throw quoteError;
      } finally {
        if (requestId === requestSequenceRef.current) setLoading(false);
      }
    },
    [cartItems, customer, requestedBonusKopecks],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      refreshQuote({ bonusKopecks: requestedBonusKopecks, signal: controller.signal }).catch(
        (quoteError) => {
          if (quoteError?.code !== "ERR_CANCELED") {
            console.error("Не вдалося отримати loyalty quote:", quoteError);
          }
        },
      );
    }, 220);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [cartSignature, customer?._id, requestedBonusKopecks, refreshQuote, sharedSettings?.revision]);

  const useMaximum = useCallback(() => {
    setRequestedBonusKopecksState(toNonNegativeInteger(quote?.maxRedeemableBonusKopecks));
  }, [quote?.maxRedeemableBonusKopecks]);

  const cancelRedemption = useCallback(() => {
    setRequestedBonusKopecksState(0);
  }, []);

  return {
    settings: sharedSettings || settings,
    quote,
    requestedBonusKopecks,
    loading,
    error,
    conditionsNotice,
    setRequestedBonusKopecks,
    useMaximum,
    cancelRedemption,
    refreshQuote,
  };
};

export default useCheckoutLoyalty;
